import React from 'react';
import { styled } from '@compiled/react';
import isNil from 'lodash/isNil';
import Avatar from '@atlaskit/avatar';
import TeamsIcon from '@atlaskit/icon/core/teams';
import PeopleIconOld from '@atlaskit/icon/glyph/people';
import { token } from '@atlaskit/tokens';
import { VerifiedTeamIcon } from '@atlaskit/people-teams-ui-public/verified-team-icon';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { SWIMLANE_TEAMLESS } from '@atlassian/jira-portfolio-3-plan-increment-common/src/common/constants.tsx';
import {
	type MenuItem,
	MenuNodeType,
} from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import { useTextFilter } from '@atlassian/jira-software-context-menu/src/common/utils/use-text-filter/index.tsx';
import type { Team } from '../../../../../../../../model/team/team-types.tsx';
import { getSwimlaneId } from '../../../../../../../../services/issue/issue-data-transformer.tsx';
import { issueChangeTeamRequest } from '../../../../../../../../state/actions/issue/change-team/index.tsx';
import { useBoardSelector, useBoardActionCreator } from '../../../../../../../../state/index.tsx';
import { boardIssuesSelector } from '../../../../../../../../state/selectors/issue/board-issue-selectors.tsx';
import { getTeams } from '../../../../../../../../state/selectors/software/software-selectors.tsx';
import { getSwimlaneMode } from '../../../../../../../../state/selectors/swimlane/swimlane-mode-selectors.tsx';
import { getSwimlanes } from '../../../../../../../../state/selectors/swimlane/swimlane-selectors.tsx';
import { CHANGE_TEAM_MENU_ID, CHANGE_TEAM_UNASSIGN_MENU_ID } from '../../constants.tsx';
import messages from '../../messages.tsx';
import type { Props } from './types.tsx';
import { useChangeTeamContent } from './view.tsx';

/**
 * Get team icon for increment planning board
 * @param imageUrl team avatar URL
 * @param name team name
 * @returns
 */
const getTeamIcon = (imageUrl?: string | null) => {
	if (imageUrl) {
		return <Avatar size="small" src={imageUrl} />;
	}
	return (
		<PeopleIconWrapper>
			<TeamsIcon
				label=""
				LEGACY_fallbackIcon={PeopleIconOld}
				LEGACY_size="small"
				color={token('color.icon')}
			/>
		</PeopleIconWrapper>
	);
};

const getTeamVerifiedIcon = (team: Team) => {
	if (team.verified && fg('plans_managed_teams_indicator_program_enabled')) {
		return <VerifiedTeamIcon showTooltip />;
	}
	return null;
};

export const useChangeTeam = ({ issueId, selectedCardIds }: Props) => {
	const { formatMessage } = useIntl();
	const allIssues = useBoardSelector(boardIssuesSelector);
	const triggerIssue = allIssues[String(issueId)];
	const swimlaneMode = useBoardSelector(getSwimlaneMode);
	const swimlaneId = useBoardSelector((state) =>
		!isNil(triggerIssue) ? getSwimlaneId(swimlaneMode, getSwimlanes(state), triggerIssue) : null,
	);
	const teams = Object.values(useBoardSelector(getTeams)).filter(
		({ isPlanTeam, id }) => id !== swimlaneId && isPlanTeam,
	);

	const options: MenuItem[] = teams.map((team) => ({
		elemAfterLabel: getTeamVerifiedIcon(team),
		icon: getTeamIcon(team.avatarUrl),
		id: `${CHANGE_TEAM_MENU_ID}/${team.id}`,
		label: team.name,
		onClick: () => onChangeTeam(team),
		type: MenuNodeType.Item,
	}));

	const unassignOption: MenuItem = {
		id: CHANGE_TEAM_UNASSIGN_MENU_ID,
		label: formatMessage(messages.unassign),
		onClick: () => onChangeTeam(),
		type: MenuNodeType.Item,
	};

	if (!isNil(triggerIssue.teamId)) {
		options.unshift(unassignOption);
	}

	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const transformOptionToString = ({ label }: MenuItem) => label as string;

	const [filteredOptions, updateFilter] = useTextFilter<MenuItem>(options, transformOptionToString);

	const onChangeTeam = useBoardActionCreator((destinationTeam?: Team) =>
		issueChangeTeamRequest({
			issueIds: selectedCardIds,
			sourceColumnId: triggerIssue.columnId,
			destinationColumnId: triggerIssue.columnId,
			rankBeforeIssueId: null,
			rankAfterIssueId: null,
			sourceSwimlaneId: swimlaneId,
			destinationSwimlaneId: destinationTeam === undefined ? SWIMLANE_TEAMLESS : destinationTeam.id,
		}),
	);

	return useChangeTeamContent({
		setQuery: updateFilter,
		options: filteredOptions,
	});
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const PeopleIconWrapper = styled.div({
	borderRadius: '50%',
	paddingTop: token('space.025'),
	paddingRight: token('space.050'),
	paddingBottom: token('space.025'),
	paddingLeft: token('space.050'),
	backgroundColor: token('color.background.neutral'),
	marginTop: 0,
	marginRight: token('space.025'),
	marginBottom: 0,
	marginLeft: token('space.025'),
});
